import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../../../web/components-v2/Button';
import { borderColor, grayIconFill, gray4, header, navigation } from '../../../../../../web/styles/colors';
import { SavedMessage } from '../../types/savedMessages';

interface MessageListItemProps {
	message: SavedMessage;
	index: number;
	onEdit: () => void;
	onDelete: () => void;
	onReorder: (messages: SavedMessage[]) => void;
	allMessages: SavedMessage[];
}

interface MessageDropData extends Record<string, unknown> {
	type: 'message';
	id: string;
	index: number;
	message: SavedMessage;
}

export const MessageListItem: React.FC<MessageListItemProps> = ({
	message,
	index,
	onEdit,
	onDelete,
	onReorder,
	allMessages,
}) => {
	const itemRef = React.useRef<HTMLDivElement>(null);
	const dragHandleRef = React.useRef<HTMLDivElement>(null);
	const [isDragging, setIsDragging] = React.useState(false);
	const [closestEdge, setClosestEdge] = React.useState<string | null>(null);

	React.useEffect(() => {
		const element = itemRef.current;
		const handle = dragHandleRef.current;
		if (!element || !handle) return;

		const cleanup = combine(
			draggable({
				element: handle,
				getInitialData: (): MessageDropData => ({
					type: 'message',
					id: message.id,
					index,
					message,
				}),
				onDragStart: () => setIsDragging(true),
				onDrop: () => setIsDragging(false),
			}),
			dropTargetForElements({
				element,
				canDrop: ({ source }) => source.data.type === 'message' && source.data.id !== message.id,
				getData: args => {
					return attachClosestEdge(
						{ type: 'message', id: message.id, index, message },
						{
							element,
							input: args.input,
							allowedEdges: ['top', 'bottom'],
						}
					);
				},
				onDragEnter: args => {
					if (args.source.data.type === 'message') {
						const edge = extractClosestEdge(args.self.data);
						setClosestEdge(edge);
					}
				},
				onDragLeave: () => {
					setClosestEdge(null);
				},
				onDrop: args => {
					setClosestEdge(null);
					if (args.source.data.type !== 'message') return;

					const sourceIndex = args.source.data.index as number;
					const targetIndex = index;
					const edge = extractClosestEdge(args.self.data);

					// Calculate new position based on edge
					let newIndex = targetIndex;
					if (edge === 'bottom') {
						newIndex = sourceIndex < targetIndex ? targetIndex : targetIndex + 1;
					} else {
						newIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
					}

					// Reorder the messages
					const reordered = [...allMessages];
					const [removed] = reordered.splice(sourceIndex, 1);
					reordered.splice(newIndex, 0, removed);

					onReorder(reordered);
				},
			})
		);

		return cleanup;
	}, [message, index, allMessages, onReorder]);

	const truncateContent = (content: string, maxLength = 100) => {
		if (content.length <= maxLength) return content;
		return `${content.substring(0, maxLength)}...`;
	};

	return (
		<div
			ref={itemRef}
			className={css(
				s.messageItem,
				isDragging && s.messageItemDragging,
				closestEdge === 'top' && s.messageItemDropTop,
				closestEdge === 'bottom' && s.messageItemDropBottom
			)}
		>
			<div ref={dragHandleRef} className={css(s.dragHandle)} title='Drag to reorder'>
				⋮⋮
			</div>
			<div className={css(s.messageContent)}>
				<div className={css(s.messageLabel)}>{message.label}</div>
				<div className={css(s.messagePreview)}>{truncateContent(message.content)}</div>
			</div>
			<div className={css(s.messageActions)}>
				<Button variant='ghost' size='sm' onClick={onEdit}>
					Edit
				</Button>
				<Button variant='destructive-secondary' size='sm' onClick={onDelete}>
					Delete
				</Button>
			</div>
		</div>
	);
};

const s = StyleSheet.create({
	messageItem: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: '12px',
		padding: '12px',
		border: `1px solid ${borderColor}`,
		borderRadius: '4px',
		backgroundColor: 'white',
		transition: 'all 0.2s',
		position: 'relative',
		':hover': {
			borderColor: navigation,
			backgroundColor: '#F9F9F9',
		},
	},
	messageItemDragging: {
		opacity: 0.5,
	},
	messageItemDropTop: {
		'::before': {
			content: '""',
			position: 'absolute',
			top: '-2px',
			left: 0,
			right: 0,
			height: '2px',
			backgroundColor: '#0066cc',
		},
	},
	messageItemDropBottom: {
		'::after': {
			content: '""',
			position: 'absolute',
			bottom: '-2px',
			left: 0,
			right: 0,
			height: '2px',
			backgroundColor: '#0066cc',
		},
	},
	dragHandle: {
		cursor: 'grab',
		color: grayIconFill,
		fontSize: '16px',
		lineHeight: '1',
		padding: '4px',
		userSelect: 'none',
		':hover': {
			color: header,
		},
		':active': {
			cursor: 'grabbing',
		},
	},
	messageContent: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: '4px',
		minWidth: 0,
	},
	messageLabel: {
		fontWeight: 500,
		fontSize: '14px',
	},
	messagePreview: {
		fontSize: '12px',
		color: gray4,
		lineHeight: '1.4',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	messageActions: {
		display: 'flex',
		gap: '8px',
		flexShrink: 0,
	},
});
